import { httpResource } from '@angular/common/http';
import { Component, computed, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormRecord,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { GeneralDto } from '@api-types/dtos/create-analysis-configuration.dto';
import { AnalysisMissingValueStrategy } from '@api-types/enums/analysis-missing-value-strategy.enum';
import { AnalysisType } from '@api-types/enums/analysis-type.enum';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { ControlsOf } from '@api-types/helper/helper';
import { ForecastVersionResponse } from '@api-types/responses/forecast-version.response';
import { selectIsLoggedInUserAdmin } from '@core/store/auth/auth.selectors';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { environment } from '@environments/environment';
import {
  FormFieldComponent,
  FormInputCheckboxComponent,
  FormInputNumberComponent,
  FormInputSelectComponent,
  FormInputSelectedDirective,
  FormInputSelectionDirective,
  FormInputTextComponent,
  PmIconComponent,
  PmSelectionCardComponent,
} from '@libs/ui';
import { LucideInfo, LucideLock } from '@lucide/angular';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CyclesStore } from 'consensus/cycles/services/cycles.store';
import { Cycle, CycleStatus } from 'consensus/cycles/types/cycle.type';
import { CreateAnalysisConfigurationBaseStepComponent } from 'demand-forecasting/analysis/components/configuration/steps/base-create-analysis-configuration-step-component.interface';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { selectConfigurationId } from 'demand-forecasting/store/router-state/router.selectors';

const requiredWhenRolling: ValidatorFn = (control) => {
  const parent = control.parent as FormGroup | null;
  if (!parent) return null;
  const analysisType = parent.get('analysisType')?.value as
    | AnalysisType
    | undefined;
  if (analysisType !== AnalysisType.ROLLING) return null;
  return Validators.required(control) ?? Validators.min(1)(control);
};

const anchorRequiredWhenSinglePeriod: ValidatorFn = (group) => {
  if (group.get('analysisType')?.value !== AnalysisType.SINGLE_PERIOD) {
    return null;
  }
  const hasAnchor =
    !!group.get('consensusCycleId')?.value ||
    !!group.get('forecastVersionId')?.value;
  return hasAnchor ? null : { anchorRequired: true };
};

@Component({
  selector: 'pm-general-step-analysis-configuration',
  templateUrl: './general-step-analysis-configuration.component.html',
  imports: [
    MatRadioModule,
    TranslateModule,
    ReactiveFormsModule,
    PmSelectionCardComponent,
    PmIconComponent,
    FormInputCheckboxComponent,
    FormFieldComponent,
    FormInputSelectComponent,
    FormInputSelectionDirective,
    FormInputSelectedDirective,
    FormInputTextComponent,
    FormInputNumberComponent,
  ],
})
export class GeneralStepAnalysisConfigurationComponent
  extends CreateAnalysisConfigurationBaseStepComponent<GeneralDto>
  implements OnInit
{
  private readonly analysisStateService = inject(AnalysisStateService);
  private readonly translate = inject(TranslateService);
  private readonly dateUtilsService = inject(DateUtilsService);
  private readonly store = inject(Store);
  private readonly cyclesStore = inject(CyclesStore);

  protected readonly AnalysisType = AnalysisType;
  protected readonly LockIcon = LucideLock;
  protected readonly InfoIcon = LucideInfo;

  ngOnInit(): void {
    super.ngOnInit();

    this.cyclesStore.reload();
  }

  private readonly configurationId = this.store.selectSignal(
    selectConfigurationId,
  );
  private readonly isAdmin = this.store.selectSignal(selectIsLoggedInUserAdmin);

  protected readonly analysisTypeOptions = [
    {
      value: AnalysisType.ROLLING,
      titleKey: 'ANALYSIS.CREATE_CONFIGURATION.TRACK_ROLLING',
      descriptionKey: 'ANALYSIS.CREATE_CONFIGURATION.TRACK_ROLLING_DESCRIPTION',
    },
    {
      value: AnalysisType.SINGLE_PERIOD,
      titleKey: 'ANALYSIS.CREATE_CONFIGURATION.TRACK_SINGLE',
      descriptionKey: 'ANALYSIS.CREATE_CONFIGURATION.TRACK_SINGLE_DESCRIPTION',
    },
  ];

  protected readonly comparisonTimeseriesNames = this.store.selectSignal(
    ConfigurationsSelectors.selectAdditionalTimeSeriesNames,
  );

  protected readonly missingValueStrategyOptions = [
    {
      value: AnalysisMissingValueStrategy.NEAREST_EARLIER,
      labelKey: 'ANALYSIS.CREATE_CONFIGURATION.SERIES_HAS_NO_VALUE_NEAREST',
    },
    {
      value: AnalysisMissingValueStrategy.SKIP_MONTH,
      labelKey: 'ANALYSIS.CREATE_CONFIGURATION.SERIES_HAS_NO_VALUE_SKIP',
    },
  ];

  private readonly forecastVersionsResource = httpResource<
    ForecastVersionResponse[]
  >(() => {
    const configurationId = this.configurationId();
    if (configurationId === undefined) {
      return undefined;
    }
    return `${environment.api.url}/results/of-configuration/${configurationId}`;
  });

  protected readonly consensusCycleOptions = computed(() => {
    const configurationId = this.configurationId();
    const cycles = this.cyclesStore.cycles();

    if (configurationId === undefined) {
      return [];
    }

    return cycles
      .filter(
        (cycle) =>
          cycle.forecastConfigurationId === configurationId &&
          cycle.status === CycleStatus.Finished,
      )
      .map((cycle) => {
        const suffix = this.cycleSuffix(cycle);
        return {
          id: cycle.id,
          name: cycle.name,
          suffix,
          label: `${cycle.name} - ${suffix}`,
          forecastVersionId: cycle.forecastVersionId,
          sortDate: (cycle.endDate ?? cycle.startDate).getTime(),
        };
      });
  });

  protected readonly forecastVersionOptions = computed(() => {
    const admin = this.isAdmin();
    return (this.forecastVersionsResource.value() ?? [])
      .filter(
        (version) =>
          version.status === ForecastVersionStatus.FINISHED &&
          (admin || version.isVisible),
      )
      .map((version) => ({
        id: version.id,
        label: this.dateUtilsService.formatDateBasedOnLanguage(
          version.createdAt,
        ),
        createdAt: version.createdAt,
      }));
  });

  form: FormGroup<ControlsOf<GeneralDto>> = this.fb.group(
    {
      name: this.fb.control('', {
        validators: [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(32),
          (control) => {
            if (!this.analysisStateService.configurations.hasValue()) {
              return null;
            }
            return this.analysisStateService.configurations.value().some(
              (c) =>
                c.name.trim().toLowerCase() ===
                String(control.value ?? '')
                  .trim()
                  .toLowerCase(),
            )
              ? { nameAlreadyUsed: true }
              : null;
          },
        ],
        nonNullable: true,
      }),
      analysisType: this.fb.control(AnalysisType.ROLLING, {
        validators: [Validators.required],
        nonNullable: true,
      }),
      freezePeriod: this.fb.control(2, {
        validators: [requiredWhenRolling],
        nonNullable: true,
      }),
      lookbackPeriod: this.fb.control(12, {
        validators: [requiredWhenRolling],
        nonNullable: true,
      }),
      comparisonTimeseriesNames: this.fb.control<string[]>([], {
        nonNullable: true,
      }),
      missingValueStrategy: this.fb.control(
        AnalysisMissingValueStrategy.NEAREST_EARLIER,
        { nonNullable: true },
      ),
      consensusCycleId: this.fb.control<string | null>(null),
      forecastVersionId: this.fb.control<string | null>(null),
    },
    { validators: [anchorRequiredWhenSinglePeriod] },
  );

  protected readonly analysisType = toSignal(
    this.form.controls.analysisType.valueChanges,
    { initialValue: this.form.controls.analysisType.value },
  );

  private readonly selectedConsensusCycleId = toSignal(
    this.form.controls.consensusCycleId.valueChanges,
    { initialValue: this.form.controls.consensusCycleId.value },
  );

  protected readonly selectedConsensusCycle = computed(() =>
    this.consensusCycleOptions().find(
      (option) => option.id === this.selectedConsensusCycleId(),
    ),
  );

  protected readonly selectedForecastVersionId = toSignal(
    this.form.controls.forecastVersionId.valueChanges,
    { initialValue: this.form.controls.forecastVersionId.value },
  );

  protected readonly evaluationInfoKey = computed(() => {
    const hasCycle = !!this.selectedConsensusCycleId();
    const hasVersion = !!this.selectedForecastVersionId();

    if (hasCycle && hasVersion) {
      return 'ANALYSIS.CREATE_CONFIGURATION.WHAT_TO_EVALUATE_INFO_CYCLE_AND_VERSION';
    }
    if (hasCycle) {
      return 'ANALYSIS.CREATE_CONFIGURATION.WHAT_TO_EVALUATE_INFO_CYCLE_ONLY';
    }
    if (hasVersion) {
      return 'ANALYSIS.CREATE_CONFIGURATION.WHAT_TO_EVALUATE_INFO_VERSION_ONLY';
    }
    return null;
  });

  private readonly selectedComparisonTimeseries = toSignal(
    this.form.controls.comparisonTimeseriesNames.valueChanges,
    { initialValue: this.form.controls.comparisonTimeseriesNames.value },
  );

  protected readonly comparisonTimeseriesNamesForm = new FormRecord<
    FormControl<boolean>
  >({});

  constructor() {
    super();

    effect(() => {
      const names = this.comparisonTimeseriesNames();
      const selected = this.selectedComparisonTimeseries();
      const record = this.comparisonTimeseriesNamesForm;

      Object.keys(record.controls)
        .filter((name) => !names.includes(name))
        .forEach((name) => record.removeControl(name, { emitEvent: false }));

      names.forEach((name) => {
        const isSelected = selected.includes(name);
        const control = record.controls[name];
        if (!control) {
          record.addControl(
            name,
            new FormControl(isSelected, { nonNullable: true }),
            { emitEvent: false },
          );
          return;
        }
        if (control.value !== isSelected) {
          control.setValue(isSelected, { emitEvent: false });
        }
      });
    });

    this.form.controls.consensusCycleId.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((cycleId) => {
        const cycle = this.consensusCycleOptions().find(
          (option) => option.id === cycleId,
        );
        const versionExists = this.forecastVersionOptions().some(
          (option) => option.id === cycle?.forecastVersionId,
        );
        if (cycle && versionExists) {
          this.form.controls.forecastVersionId.setValue(
            cycle.forecastVersionId,
          );
        }
      });

    this.comparisonTimeseriesNamesForm.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) =>
        this.form.controls.comparisonTimeseriesNames.setValue(
          Object.entries(value)
            .filter(([, isSelected]) => isSelected)
            .map(([name]) => name),
        ),
      );
  }

  protected selectAnalysisType(value: AnalysisType): void {
    this.form.controls.analysisType.setValue(value);
    if (value === AnalysisType.SINGLE_PERIOD) {
      this.form.controls.comparisonTimeseriesNames.setValue([]);
    }
    this.form.controls.freezePeriod.updateValueAndValidity();
    this.form.controls.lookbackPeriod.updateValueAndValidity();
  }

  private cycleSuffix(cycle: Cycle): string {
    return this.translate.instant('ANALYSIS.CREATE_CONFIGURATION.CLOSED', {
      date: this.dateUtilsService.formatDateBasedOnLanguage(
        cycle.endDate?.toString(),
      ),
    });
  }
}
